import React, { useState, useEffect } from 'react';
import {
    resolveScreenshotUrl,
    refreshScreenshotUrl,
    normalizeScreenshotInput,
} from '../../../../utils/screenshotUtils';
import ImagePreviewModal from '../../../Modal/ImagePreviewModal';
import ScreenshotPlaceholder from './ScreenshotPlaceholder';
import styles from '../../../../styles/automationStudio/automationStudio.module.scss';

export default function ScreenshotPreview({
    screenshotRaw,
    fileName = 'Step screenshot',
    emptyVariant = 'empty',
    showWhenEmpty = true,
}) {
    const [imgError, setImgError] = useState(false);
    const [resolvedUrl, setResolvedUrl] = useState(null);
    const [isResolving, setIsResolving] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const normalizedPath = normalizeScreenshotInput(screenshotRaw);

    useEffect(() => {
        let cancelled = false;
        setImgError(false);
        if (!normalizedPath) {
            setResolvedUrl(null);
            setIsResolving(false);
            return undefined;
        }
        setIsResolving(true);
        resolveScreenshotUrl(normalizedPath)
            .then(({ url }) => {
                if (!cancelled) {
                    setResolvedUrl(url || normalizedPath);
                    setIsResolving(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setResolvedUrl(normalizedPath);
                    setIsResolving(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [normalizedPath]);

    const renderPlaceholder = (variant) => (
        <div className={styles.verificationScreenshotPanel}>
            <div className={styles.verificationScreenshotCard}>
                <ScreenshotPlaceholder variant={variant} size="card" />
            </div>
        </div>
    );

    if (!normalizedPath) {
        if (!showWhenEmpty) return null;
        return renderPlaceholder(emptyVariant);
    }

    const showImage = Boolean(resolvedUrl && !imgError && !isResolving);
    const placeholderVariant = imgError ? 'unavailable' : 'loading';

    return (
        <div className={styles.verificationScreenshotPanel}>
            <div className={styles.verificationScreenshotCard}>
                {showImage ? (
                    <div
                        className={`${styles.verificationScreenshotImageWrap} ${styles.verificationScreenshotImageWrapInteractive}`}
                        onClick={() => setPreviewOpen(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPreviewOpen(true);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Open screenshot preview"
                    >
                        <img
                            src={resolvedUrl}
                            className={styles.verificationScreenshotImage}
                            alt="Step screenshot"
                            loading="lazy"
                            onError={async () => {
                                if (!normalizedPath) {
                                    setImgError(true);
                                    return;
                                }
                                const { url } = await refreshScreenshotUrl(normalizedPath);
                                if (url) {
                                    setResolvedUrl(url);
                                    setImgError(false);
                                } else {
                                    setImgError(true);
                                }
                            }}
                        />
                    </div>
                ) : (
                    <ScreenshotPlaceholder variant={placeholderVariant} size="card" />
                )}
            </div>
            <ImagePreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                imageUrl={resolvedUrl}
                fileName={fileName}
                onImageError={async () => {
                    if (!normalizedPath) return null;
                    const { url } = await refreshScreenshotUrl(normalizedPath);
                    if (url) {
                        setResolvedUrl(url);
                        setImgError(false);
                        return url;
                    }
                    return null;
                }}
            />
        </div>
    );
}
